import { useEffect, useRef, useState } from "react";
import { Camera, File, Image, Paperclip, Send } from "lucide-react";
import PlayerVoiceRequest from "./PlayerVoiceRequest";
import { useI18n } from "../lib/i18n";
import { focusPlayerRequestComposerWithoutScroll } from "../lib/playerRequestViewport";

export default function PlayerRequestComposer({
  value, onChange, onSend, requestId, contextTitle, onVoiceSent, onAttachment, busy = false,
}) {
  const { t } = useI18n();
  const inputRef = useRef(null); const attachmentAnchorRef = useRef(null);
  const photoLibraryRef = useRef(null); const cameraRef = useRef(null); const fileRef = useRef(null);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const hasText = Boolean(value.trim());

  const resize = (element) => {
    element.style.height = "auto";
    const nextHeight = Math.min(element.scrollHeight, 96);
    element.style.height = `${Math.max(44, nextHeight)}px`;
    element.style.overflowY = element.scrollHeight > 96 ? "auto" : "hidden";
  };

  useEffect(() => {
    if (inputRef.current) resize(inputRef.current);
  }, [value]);

  useEffect(() => {
    if (!attachmentOpen) return undefined;
    const closeOutside = (event) => {
      if (!attachmentAnchorRef.current?.contains(event.target)) setAttachmentOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [attachmentOpen]);

  const chooseAttachmentSource = (inputRefToOpen) => {
    setAttachmentOpen(false);
    inputRefToOpen.current?.click();
  };

  const attachmentSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setAttachmentOpen(false);
    if (file) onAttachment(file);
  };

  return <div
    className="relative flex items-end gap-1.5 rounded-[24px] p-0.5 min-w-0"
    style={{ background: "var(--pc-charcoal-2)", border: "1px solid var(--pc-border)" }}
    data-testid="player-request-composer"
    data-voice-active={voiceActive || undefined}
  >
    <div ref={attachmentAnchorRef} className="relative w-11 h-11 shrink-0" data-testid="composer-attachment-anchor">
      {attachmentOpen && <div
        className="absolute left-0 bottom-[calc(100%+10px)] z-10 w-[220px] max-w-[calc(100vw-28px)] rounded-2xl p-1.5"
        style={{ background: "var(--pc-charcoal-2)", border: "1px solid var(--pc-border)", boxShadow: "0 14px 34px rgba(0,0,0,.42)" }}
        role="menu"
        aria-label={t("attachment")}
        data-testid="composer-attachment-menu"
      >
        <button type="button" className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm" onClick={() => chooseAttachmentSource(photoLibraryRef)} role="menuitem" data-testid="attachment-photo-library"><Image size={17} color="var(--pc-cyan)" /> {t("photoLibrary")}</button>
        <button type="button" className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm" onClick={() => chooseAttachmentSource(cameraRef)} role="menuitem" data-testid="attachment-camera"><Camera size={17} color="var(--pc-cyan)" /> {t("takePhoto")}</button>
        <button type="button" className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm" onClick={() => chooseAttachmentSource(fileRef)} role="menuitem" data-testid="attachment-file"><File size={17} color="var(--pc-cyan)" /> {t("chooseFile")}</button>
      </div>}
      <button
        type="button"
        className="w-11 h-11 rounded-full grid place-items-center"
        style={{ color: "var(--pc-muted)" }}
        aria-label={t("attachment")}
        aria-expanded={attachmentOpen}
        disabled={busy}
        onClick={() => setAttachmentOpen((open) => !open)}
        data-testid="composer-attachment"
      ><Paperclip size={18} /></button>
      <input ref={photoLibraryRef} type="file" accept="image/*" hidden disabled={busy} onChange={attachmentSelected} data-testid="attachment-photo-library-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden disabled={busy} onChange={attachmentSelected} data-testid="attachment-camera-input" />
      <input ref={fileRef} type="file" accept="application/pdf,text/plain,.doc,.docx" hidden disabled={busy} onChange={attachmentSelected} data-testid="attachment-file-input" />
    </div>
    <textarea
      ref={inputRef}
      rows="1"
      value={value}
      disabled={busy}
      onPointerDown={(event) => {
        focusPlayerRequestComposerWithoutScroll(event.currentTarget, document.activeElement);
      }}
      onChange={(event) => { resize(event.currentTarget); onChange(event.target.value); }}
      placeholder={t("messageComposerPlaceholder")}
      className="player-request-composer-input min-w-0 flex-1 resize-none bg-transparent border-0 outline-none px-1 py-2.5 text-sm leading-5"
      style={{ minHeight: 44, maxHeight: 96, color: "var(--pc-text)" }}
      data-testid="composer-input"
    />
    {hasText && !voiceActive ? <button
      type="button"
      disabled={busy}
      onClick={onSend}
      className="w-11 h-11 shrink-0 rounded-full grid place-items-center"
      style={{
        background: "var(--pc-cyan)", color: "#001014",
        opacity: busy ? 0.55 : 1,
      }}
      aria-label={t("send")}
      data-testid="composer-send"
    >
      <Send size={17} />
    </button> : <PlayerVoiceRequest requestId={requestId} contextTitle={contextTitle} onSent={onVoiceSent}
      disabled={busy} onActivityChange={(active) => { setVoiceActive(active); if (active) setAttachmentOpen(false); }}
      triggerProps={{ "data-testid": "composer-mic" }} />}
  </div>;
}
